/* SSi website – About page sections.
 *
 * One big file for the About page because the sections are one-off –
 * not reused anywhere else. Uses the shared Header + Footer via
 * Blocks.jsx.
 *
 * Layout order:
 *   AboutHero     – intro: who we are, what this is
 *   AboutMission  – our mission / why we exist (the Welsh activism story)
 *   AboutTeam     – grid of people; portraits + bios
 *   AboutFounders – feature on Catrin & Aran with the big portrait
 *   AboutJoin     – gentle CTA strip back into the product
 */

import { Link } from 'react-router-dom';

/* ─────────────────────────────── Hero ─────────────────────────────── */
export function AboutHero({ t }) {
  return (
    <section style={aboutStyles.heroWrap}>
      <div style={aboutStyles.heroInner}>
        <div style={aboutStyles.heroKicker}>{t.aboutHeroKicker}</div>
        <h1 style={aboutStyles.heroH1}>{t.aboutHeroTitle}</h1>
        <p className="ssi-about__heroLede" style={aboutStyles.heroLede}>{t.aboutHeroLede}</p>
      </div>
    </section>
  );
}

/* ─────────────────────────────── Mission ─────────────────────────────── */
export function AboutMission({ t }) {
  return (
    <section style={aboutStyles.missionWrap}>
      <div className="ssi-about__missionInner" style={aboutStyles.missionInner}>
        <div className="ssi-about__missionCol1" style={aboutStyles.missionCol1}>
          <div style={aboutStyles.kicker}>{t.aboutMissionKicker}</div>
          <h2 style={aboutStyles.h2}>{t.aboutMissionTitle}</h2>
        </div>
        <div style={aboutStyles.missionCol2}>
          <p style={aboutStyles.p}>{t.aboutMissionP1}</p>
          <p style={aboutStyles.p}>{t.aboutMissionP2}</p>
          <p style={aboutStyles.p}>{t.aboutMissionP3}</p>
        </div>
      </div>

      {/* Stat row – three headline numbers */}
      <div className="ssi-about__statsRow" style={aboutStyles.statsRow}>
        <Stat n={t.aboutStat1N} l={t.aboutStat1L}/>
        <Stat n={t.aboutStat2N} l={t.aboutStat2L}/>
        <Stat n={t.aboutStat3N} l={t.aboutStat3L}/>
      </div>
    </section>
  );
}

function Stat({ n, l }) {
  return (
    <div style={aboutStyles.stat}>
      <div className="ssi-about__statN" style={aboutStyles.statN}>{n}</div>
      <div style={aboutStyles.statL}>{l}</div>
    </div>
  );
}

/* ─────────────────────────────── Team grid ─────────────────────────────── */
export function AboutTeam({ t }) {
  const people = [
    { name: t.teamPerson1Name, role: t.teamPerson1Role, bio: t.teamPerson1Bio, img: t.teamPerson1Img },
    { name: t.teamPerson2Name, role: t.teamPerson2Role, bio: t.teamPerson2Bio, img: t.teamPerson2Img },
    { name: t.teamPerson3Name, role: t.teamPerson3Role, bio: t.teamPerson3Bio, img: t.teamPerson3Img },
    { name: t.teamPerson4Name, role: t.teamPerson4Role, bio: t.teamPerson4Bio, img: t.teamPerson4Img },
    { name: t.teamPerson5Name, role: t.teamPerson5Role, bio: t.teamPerson5Bio, img: t.teamPerson5Img },
    { name: t.teamPerson6Name, role: t.teamPerson6Role, bio: t.teamPerson6Bio, img: t.teamPerson6Img },
    { name: t.teamPerson7Name, role: t.teamPerson7Role, bio: t.teamPerson7Bio, img: t.teamPerson7Img },
    { name: t.teamPerson8Name, role: t.teamPerson8Role, bio: t.teamPerson8Bio, img: t.teamPerson8Img },
    { name: t.teamPerson9Name, role: t.teamPerson9Role, bio: t.teamPerson9Bio, img: t.teamPerson9Img },
  ];

  return (
    <section style={aboutStyles.teamWrap}>
      <div style={aboutStyles.teamHead}>
        <div style={aboutStyles.kicker}>{t.aboutTeamKicker}</div>
        <h2 style={aboutStyles.h2}>{t.aboutTeamTitle}</h2>
        <p style={aboutStyles.leadP}>{t.aboutTeamLede}</p>
      </div>
      <ul className="ssi-about__teamGrid" style={aboutStyles.teamGrid}>
        {people.map((p, i) => (
          <li key={i} style={aboutStyles.personCard}>
            <div style={aboutStyles.portrait}>
              {p.img
                ? <img src={p.img} alt="" style={aboutStyles.portraitImg}/>
                : <PortraitPlaceholder name={p.name}/>}
            </div>
            <div style={aboutStyles.pName}>{p.name}</div>
            <div style={aboutStyles.pRole}>{p.role}</div>
            <p style={aboutStyles.pBio}>{p.bio}</p>
          </li>
        ))}
      </ul>
    </section>
  );
}

/* Initials placeholder portrait – warm tile, big display-font initials.
 * Honest about being a placeholder; avoids the uncanny-valley of a
 * drawn-face avatar when we don't have real photos yet. */
function PortraitPlaceholder({ name }) {
  const initials = (name || '')
    .split(/\s+/)
    .map(w => w[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();
  // Cycle through a small warm palette based on the name hash so the
  // grid isn't uniform.
  const palette = ['#FFE8D6', '#FFF3C4', '#FCD9D4', '#F4E5D4', '#F5D7D0'];
  const hash = (name || '').split('').reduce((a, c) => a + c.charCodeAt(0), 0);
  const bg = palette[hash % palette.length];
  return (
    <div style={{ ...aboutStyles.portraitPh, background: bg }}>
      <span style={aboutStyles.portraitInitials}>{initials}</span>
      <span style={aboutStyles.portraitTag}>Photo to come</span>
    </div>
  );
}

/* ─────────────────────────────── Founders feature ─────────────────────────────── */
export function AboutFounders({ t }) {
  return (
    <section style={aboutStyles.foundersWrap}>
      <div className="ssi-about__foundersInner" style={aboutStyles.foundersInner}>
        <div className="ssi-about__foundersPortrait" style={aboutStyles.foundersPortrait}>
          <img src="/assets/aran-catrin-update.jpeg" alt="Catrin and Aran" style={aboutStyles.foundersImg}/>
        </div>
        <div className="ssi-about__foundersCopy" style={aboutStyles.foundersCopy}>
          <div style={aboutStyles.kickerOnRed}>{t.aboutFoundersKicker}</div>
          <h2 style={aboutStyles.h2OnRed}>{t.aboutFoundersTitle}</h2>
          <p style={aboutStyles.pOnRed}>{t.aboutFoundersP1}</p>
          <p style={aboutStyles.pOnRed}>{t.aboutFoundersP2}</p>
          <blockquote style={aboutStyles.pullQuote}>
            <div style={aboutStyles.pullQuoteText}>&ldquo;{t.aboutFoundersQuote}&rdquo;</div>
            <div style={aboutStyles.pullQuoteAttr}>– {t.aboutFoundersQuoteAttr}</div>
          </blockquote>
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────── Join strip ─────────────────────────────── */
export function AboutJoin({ t }) {
  return (
    <section style={aboutStyles.joinWrap}>
      <div style={aboutStyles.joinInner}>
        <h2 style={aboutStyles.joinH2}>{t.aboutJoinTitle}</h2>
        <p style={aboutStyles.joinP}>{t.aboutJoinBody}</p>
        <div style={aboutStyles.joinCtas}>
          <Link to="/#sample" className="btn btn--primary">{t.aboutJoinCta1}</Link>
          <Link to="/contact" className="btn btn--ghost" style={{border:'1px solid var(--border-strong)'}}>{t.aboutJoinCta2}</Link>
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────── styles ─────────────────────────────── */
const aboutStyles = {
  /* shared */
  kicker: { fontSize:12, letterSpacing:'.16em', textTransform:'uppercase', color:'var(--brandRed)', fontWeight:700, marginBottom:14 },
  kickerOnRed: { fontSize:12, letterSpacing:'.16em', textTransform:'uppercase', color:'#FFD8D4', fontWeight:700, marginBottom:14 },
  h2: { fontFamily:'var(--font-display)', fontWeight:700, fontSize:44, lineHeight:1.08, letterSpacing:'-.01em', margin:'0 0 18px', textWrap:'balance' },
  h2OnRed: { fontFamily:'var(--font-display)', fontWeight:700, fontSize:44, lineHeight:1.08, letterSpacing:'-.01em', margin:'0 0 18px', color:'#fff', textWrap:'balance' },
  p: { fontSize:17, lineHeight:1.6, color:'var(--fg-1)', margin:'0 0 20px', maxWidth:'60ch' },
  pOnRed: { fontSize:17, lineHeight:1.6, color:'rgba(255,255,255,.92)', margin:'0 0 20px', maxWidth:'60ch' },
  leadP: { fontSize:18, lineHeight:1.55, color:'var(--grey)', margin:0, maxWidth:'60ch', textWrap:'pretty' },

  /* hero */
  heroWrap: { background:'var(--offWhite, #FDF5F5)', padding:'96px 32px 88px', borderBottom:'1px solid var(--border)' },
  heroInner: { maxWidth:1080, margin:'0 auto' },
  heroKicker: { fontSize:12, letterSpacing:'.16em', textTransform:'uppercase', color:'var(--brandRed)', fontWeight:700, marginBottom:18 },
  heroH1: { fontFamily:'var(--font-display)', fontWeight:700, fontSize:68, lineHeight:1.02, letterSpacing:'-.02em', margin:'0 0 22px', textWrap:'balance', maxWidth:18 + 'ch' },
  heroLede: { fontFamily:'var(--font-body)', fontSize:22, lineHeight:1.5, color:'var(--grey)', margin:0, maxWidth:'58ch', textWrap:'pretty' },

  /* mission */
  missionWrap: { padding:'96px 32px 48px' },
  missionInner: { maxWidth:1240, margin:'0 auto 64px', display:'grid', gridTemplateColumns:'1fr 1.3fr', gap:80 },
  missionCol1: { position:'sticky', top:28, alignSelf:'start' },
  missionCol2: {},
  statsRow: { maxWidth:1240, margin:'0 auto', display:'grid', gridTemplateColumns:'repeat(3, 1fr)', gap:32, padding:'40px 0 0', borderTop:'1px solid var(--border)' },
  stat: { textAlign:'left' },
  statN: { fontFamily:'var(--font-display)', fontWeight:700, fontSize:68, lineHeight:1, color:'var(--brandRed)', letterSpacing:'-.02em', marginBottom:10 },
  statL: { fontSize:15, color:'var(--grey)', lineHeight:1.5, maxWidth:'28ch' },

  /* team */
  teamWrap: { padding:'96px 32px 96px', background:'var(--offWhite, #FDF5F5)' },
  teamHead: { maxWidth:1240, margin:'0 auto 56px' },
  teamGrid: { listStyle:'none', padding:0, margin:'0 auto', maxWidth:1240, display:'grid', gridTemplateColumns:'repeat(3, 1fr)', gap:40 },
  personCard: { display:'flex', flexDirection:'column' },
  portrait: { aspectRatio:'4/5', borderRadius:14, overflow:'hidden', marginBottom:16, background:'#fff', border:'1px solid rgba(73,3,0,.08)' },
  portraitImg: { width:'100%', height:'100%', objectFit:'cover' },
  portraitPh: { width:'100%', height:'100%', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', gap:6 },
  portraitInitials: { fontFamily:'var(--font-display)', fontWeight:700, fontSize:56, color:'var(--brandRed)', letterSpacing:'-.02em' },
  portraitTag: { fontSize:10, letterSpacing:'.14em', textTransform:'uppercase', color:'rgba(73,3,0,.5)', fontWeight:600 },
  pName: { fontFamily:'var(--font-display)', fontWeight:700, fontSize:22, margin:'0 0 2px', letterSpacing:'-.005em' },
  pRole: { fontSize:13, letterSpacing:'.04em', color:'var(--brandRed)', fontWeight:600, marginBottom:10, textTransform:'uppercase' },
  pBio: { fontSize:14.5, lineHeight:1.55, color:'var(--fg-1)', margin:0, textWrap:'pretty' },

  /* founders */
  foundersWrap: { padding:'0 32px', background:'#fff' },
  foundersInner: {
    maxWidth:1240, margin:'0 auto',
    display:'grid', gridTemplateColumns:'1fr 1.1fr', gap:56,
    background:'var(--brandRed)', borderRadius:28, overflow:'hidden',
    alignItems:'stretch',
    transform:'translateY(-48px)',
  },
  foundersPortrait: { minHeight:480 },
  foundersImg: { width:'100%', height:'100%', objectFit:'cover', display:'block' },
  foundersCopy: { padding:'72px 56px 72px 0', display:'flex', flexDirection:'column', justifyContent:'center' },
  pullQuote: { margin:'12px 0 0', padding:'24px 0 0', borderTop:'1px solid rgba(255,255,255,.22)' },
  pullQuoteText: { fontFamily:'var(--font-display)', fontStyle:'italic', fontSize:22, lineHeight:1.35, color:'#fff', marginBottom:10, textWrap:'pretty' },
  pullQuoteAttr: { fontSize:13, color:'rgba(255,255,255,.75)', letterSpacing:'.02em' },

  /* join */
  joinWrap: { padding:'32px 32px 120px', background:'#fff' },
  joinInner: { maxWidth:760, margin:'0 auto', textAlign:'center' },
  joinH2: { fontFamily:'var(--font-display)', fontWeight:700, fontSize:44, lineHeight:1.08, letterSpacing:'-.01em', margin:'0 0 18px', textWrap:'balance' },
  joinP: { fontSize:18, lineHeight:1.55, color:'var(--grey)', margin:'0 0 32px', textWrap:'pretty' },
  joinCtas: { display:'flex', gap:12, justifyContent:'center', flexWrap:'wrap' },
};
